import { configSchema } from "@/src/utils/get-config"
import { handleError } from "@/src/utils/handle-error"
import { logger } from "@/src/utils/logger"
import {
  registryIndexSchema,
  RegistryItem,
  registryItemSchema,
  registryResolvedItemsTreeSchema,
} from "@/src/utils/registry/schema"
import deepmerge from "deepmerge"
import { HttpsProxyAgent } from "https-proxy-agent"
import fetch, { Response } from "node-fetch"
import { z } from "zod"
import { getProjectInfo } from "@/src/utils/get-project-info"
import { Framework, FRAMEWORKS } from "@/src/utils/frameworks"
import { colors } from "@/src/utils/colors"
import { tokenStorage } from "@/src/utils/bootstrap"

export const REGISTRY_URL = "https://template.tiptap.dev"
// export const REGISTRY_URL = "https://template-preview.tiptap.dev"
// export const REGISTRY_URL = "http://localhost:3000"

const agent = process.env.https_proxy
  ? new HttpsProxyAgent(process.env.https_proxy)
  : undefined

export async function getRegistryIndex() {
  try {
    const [result] = await fetchRegistry(["index.json"])

    return registryIndexSchema.parse(result)
  } catch (error) {
    logger.error("\n")
    handleError(error)
  }
}

function createRegistryError(response: Response, url?: string): Error {
  const urlInfo = url ? ` at ${colors.cyan(url)}` : ""

  switch (response.status) {
    case 400:
      return new Error(
        `The registry rejected the request${urlInfo}.\n` +
          `Please check the selected components and try again.`
      )

    case 401:
      tokenStorage.clear()
      return new Error(
        `You are not authorized to access the component${urlInfo}.\n` +
          `Please run ${colors.cyan("@tiptap/cli login")} to log in to your Tiptap Cloud account.`
      )

    case 404:
      return new Error(
        `The component${urlInfo} was not found.\n` +
          `It may not exist at the registry. Please make sure it is a valid component.`
      )

    case 403:
      return new Error(
        `You do not have access to the component${urlInfo}.\n` +
          `Your account may not have the required subscription plan for this component.\n` +
          `Please upgrade your subscription or use a component available in your current plan.`
      )

    case 429:
      return new Error(
        `Too many registry requests${urlInfo}.\n` +
          `Please wait a moment and try again.`
      )

    default:
      if (response.status >= 500) {
        return new Error(
          `The Tiptap registry is temporarily unavailable${urlInfo}.\n` +
            `Please try again later.`
        )
      }

      return new Error(
        `The registry returned an unexpected response${urlInfo}.\n` +
          `Please try again.`
      )
  }
}

async function fetchComponent(path: string, headers: Record<string, string>) {
  const url = getRegistryUrl(path)

  const response = await fetch(url, {
    agent,
    headers,
  })

  if (!response.ok) {
    throw createRegistryError(response, url)
  }

  return response.json()
}

async function fetchRegistryBatch(
  componentNames: string[],
  headers: Record<string, string>
) {
  const response = await fetch(
    `${REGISTRY_URL}/api/registry/components/batch`,
    {
      method: "POST",
      agent,
      headers,
      body: JSON.stringify({ components: componentNames }),
    }
  )

  if (!response.ok) {
    throw createRegistryError(response)
  }

  const results = (await response.json()) as RegistryItem[]

  return results
}

export async function fetchRegistry(paths: string[]) {
  try {
    const bearerToken = tokenStorage.getBearerToken()
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }

    if (bearerToken) {
      headers["Authorization"] = `Bearer ${bearerToken}`
    }

    const componentNames = paths.map((path) => {
      if (path.includes("/components/")) {
        return path.replace(/.*\/components\//, "").replace(".json", "")
      }
      return path.replace(".json", "")
    })

    if (componentNames.length > 1) {
      return await fetchRegistryBatch(componentNames, headers)
    }

    return Promise.all(paths.map((path) => fetchComponent(path, headers)))
  } catch (error) {
    logger.error("\n")
    handleError(error)
    return []
  }
}

export async function fetchRegistryDependencies(
  components: string[]
): Promise<string[]> {
  const response = await fetch(`${REGISTRY_URL}/api/registry/dependencies`, {
    method: "POST",
    agent,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ components }),
  })

  if (!response.ok) {
    throw createRegistryError(
      response,
      `${REGISTRY_URL}/api/registry/dependencies`
    )
  }

  return (await response.json()) as Promise<string[]>
}

export async function registryResolveItemsTree(
  names: RegistryItem["name"][],
  config: z.infer<typeof configSchema>
) {
  try {
    const index = await getRegistryIndex()
    if (!index) {
      return null
    }

    // If we're resolving the index, we want it to go first.
    if (names.includes("index")) {
      names.unshift("index")
    }

    const registryItems = await resolveRegistryItems(names)
    const result = await fetchRegistry(registryItems)
    const payload = z.array(registryItemSchema).parse(result)

    if (!payload) {
      return null
    }

    const projectInfo = await getProjectInfo(config.resolvedPaths.cwd)
    const framework = projectInfo?.framework.name as Framework["name"]

    const allDependencies = deepmerge.all(
      payload.map((item) => item.dependencies ?? [])
    )

    const allDevDependencies = deepmerge.all(
      payload.map((item) => item.devDependencies ?? [])
    )

    const filteredDevDependencies = filterDevDependenciesByFramework(
      allDevDependencies,
      framework
    )

    const dependenciesSet = new Set(
      (Array.isArray(allDependencies) ? allDependencies : []).map((dep) =>
        String(dep)
      )
    )
    const dedupedDevDependencies = filteredDevDependencies.filter(
      (dep) => !dependenciesSet.has(dep)
    )

    return registryResolvedItemsTreeSchema.parse({
      dependencies: allDependencies,
      devDependencies: dedupedDevDependencies,
      files: deepmerge.all(payload.map((item) => item.files ?? [])),
    })
  } catch (error) {
    handleError(error)
    return null
  }
}

export async function resolveRegistryItems(names: string[]) {
  const results = await fetchRegistryDependencies(names)
  const registryDependencies = results.map((name) =>
    getRegistryUrl(`components/${name}.json`)
  )

  return registryDependencies
}

function getRegistryUrl(path: string) {
  if (isUrl(path)) {
    const url = new URL(path)
    return url.toString()
  }

  if (!REGISTRY_URL) {
    throw new Error("No registry URL found")
  }

  // Keep the index.json path as is (public)
  if (path === "index.json") {
    return `${REGISTRY_URL}/r/${path}`
  }

  // Only redirect component paths to the API
  if (path.startsWith("components/")) {
    const componentName = path.replace("components/", "").replace(".json", "")
    return `${REGISTRY_URL}/api/registry/components/${componentName}`
  }

  return `${REGISTRY_URL}/${path}`
}

function isUrl(path: string) {
  try {
    new URL(path)
    return true
  } catch (error) {
    return false
  }
}

export function getRegistryTypeAliasMap() {
  return new Map<string, string>([
    ["registry:ui", "tiptapUi"],
    ["registry:ui-primitive", "tiptapUiPrimitives"],
    ["registry:ui-utils", "tiptapUiUtils"],
    ["registry:extension", "tiptapExtensions"],
    ["registry:node", "tiptapNodes"],
    ["registry:context", "contexts"],
    ["registry:hook", "hooks"],
    ["registry:lib", "lib"],
    ["registry:context", "components"],
    ["registry:template", "tiptapTemplates"],
    ["registry:component", "components"],
    ["registry:icon", "tiptapIcons"],
    ["registry:style", "styles"],
  ])
}

// Track a dependency and its parent.
export function getRegistryParentMap(registryItems: RegistryItem[]) {
  const map = new Map<string, RegistryItem>()

  registryItems.forEach((item) => {
    if (!item.registryDependencies) {
      return
    }

    item.registryDependencies.forEach((dependency) => {
      map.set(dependency, item)
    })
  })

  return map
}

/**
 * Filter development dependencies based on framework requirements
 * @param devDependencies Array of development dependencies
 * @param framework Framework name
 * @returns Filtered array of development dependencies
 */
function filterDevDependenciesByFramework(
  devDependencies: unknown,
  framework: Framework["name"]
): string[] {
  const depsArray = Array.isArray(devDependencies) ? devDependencies : []

  if (!depsArray.length) {
    return []
  }

  const stringDeps = depsArray.map((dep) => String(dep))

  if (framework) {
    const hasSass = stringDeps.includes("sass")
    const hasSassEmbedded = stringDeps.includes("sass-embedded")

    if (hasSass || hasSassEmbedded) {
      let filteredDeps = [...stringDeps]

      const prefersEmbedded: Framework["name"][] = [
        FRAMEWORKS.astro.name,
        FRAMEWORKS.laravel.name,
        FRAMEWORKS.vite.name,
        // FRAMEWORKS.remix.name,
        FRAMEWORKS["tanstack-start"].name,
        FRAMEWORKS["react-router"].name,
      ]

      const prefersRegular: Framework["name"][] = [
        FRAMEWORKS["next-app"].name,
        FRAMEWORKS["next-pages"].name,
        // FRAMEWORKS.gatsby.name,
      ]

      if (prefersEmbedded.includes(framework)) {
        filteredDeps = filteredDeps.filter((dep) => dep !== "sass")
      } else if (prefersRegular.includes(framework)) {
        filteredDeps = filteredDeps.filter((dep) => dep !== "sass-embedded")
      }

      return filteredDeps
    }
  }

  return stringDeps
}
